/**
 * `clientProjects` — `mockups/client/projects.html`'s milestone table, plus the one
 * fact the table could not state: whether a milestone the studio owes is **late**.
 *
 * **Why a read model and not a field on `MilestoneSchema`.** That schema is inside
 * `WorkOrderDetailSchema`, which is what the published `work_orders_get` answers
 * (`modules/work_orders/actions.ts`). Adding a field there would change an action's
 * output, which session 41 is not allowed to do — the session file's own
 * owner-reserved list, and `.docs/00-index.md` §"Actions are the published API".
 * `GET /api/clients/me/projects` is not an action: `work_orders_list` publishes
 * `WorkOrderSchema`, which carries no milestones at all, and no shared schema
 * described the projects list before this file. So the client's own list gets the
 * derived flag and every tool's answer stays byte for byte what it was.
 *
 * The precedent is `decisions.ts`: `ClientDecisionItemSchema` is `OpenDecisionSchema`
 * plus one derived `late` that no module stores, for the same reason and in the same
 * shape.
 */
import { z } from 'zod';
import { MilestoneSchema, WorkOrderSchema } from '../work-orders.js';
export const ClientProjectMilestoneSchema = MilestoneSchema.extend({
    late: z
        .boolean()
        .describe('The studio owes this one and its day has passed: `due_on` is before today in the ' +
        '**studio’s** zone and `delivered_at` is null. False for a delivered milestone and ' +
        'for one with no `due_on` — a milestone nobody dated cannot be late. It is the ' +
        'server’s to decide, because the browser’s clock is not the studio’s working day.'),
}).describe('One milestone as the client’s own project table draws it, plus whether it is late.');
export const ClientProjectSchema = z
    .strictObject({
    work_order: WorkOrderSchema.describe('The project itself, unchanged.'),
    milestones: z
        .array(ClientProjectMilestoneSchema)
        .describe('Its schedule in `seq` order, each row saying whether it is late.'),
})
    .describe('One project and its schedule, as the client portal lists it.');
export const ClientProjectsSchema = z
    .strictObject({
    projects: z.array(ClientProjectSchema).describe('The page, newest first.'),
    next_cursor: z.string().nullable().describe('Where the next page starts, or null at the end.'),
})
    .describe('`GET /api/clients/me/projects` — the client’s projects with their schedules.');
//# sourceMappingURL=projects.js.map